import { FormEvent, SetStateAction, useState } from 'react';
import Image from 'next/image';
import NotifyImg from '@/assets/img/notify-img.svg';
import { CustomForm } from '@/components/Formik/CustomForm';
import { CustomTextField } from '@/components/Formik/CustomTextField';
import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { themeLight } from '@/themes/light';
import { ICompany } from '@/types/api/companies';
import { IUserMembership } from '@/types/api/users';
import { handleErrorAsync } from '@/utils/handlers';
import { cnpjMask } from '@/utils/masks';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { object, string } from 'yup';
import { ICompanyData } from './View';

export const confirmDataForm = object().shape({
  short_name: string().required('O nome fantasia é obrigatório'),
});

const ConfirmDataRow = ({
  title,
  content,
}: {
  title: string;
  content?: string;
}) => (
  <Box
    component="span"
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Typography component="small" variant="caption" sx={{ opacity: '50%' }}>
      {title}
    </Typography>
    <Typography
      sx={{ textAlign: 'right' }}
      data-testid={title}
      variant="body1"
      component="strong"
    >
      {content || '-'}
    </Typography>
  </Box>
);

interface IConfirmDataProps {
  companyData: ICompanyData;
  setCompanyData: (value: SetStateAction<ICompanyData>) => void;
  onAdvancedStep: () => void;
  onSuccess: () => void;
}

const ConfirmData = ({
  companyData,
  setCompanyData,
  onAdvancedStep,
  onSuccess,
}: IConfirmDataProps) => {
  const { updateUserData, user } = useUser();
  const { setIsLoading } = useLoading();

  const [isOpenModalRegisterFullName, setIsOpenModalRegisterFullName] =
    useState(!companyData.full_name);

  const isAlreadyAffiliateToCompany = user.memberships?.find(
    membership => membership.company.id === companyData.id
  );

  const handleSubmitRegisterFullName = async (
    e: FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    const [companyDataWithFullName, error] = await handleErrorAsync(
      async () => {
        const formData = new FormData(e.target as HTMLFormElement);
        const fullName = formData.get('full_name') as string;

        if (!fullName) throw Error('Por favor insira o nome da sua empresa');

        const { data } = await updateCompany({
          id: companyData.id,
          full_name: fullName,
        });

        return data;
      }
    );

    if (error) return Toast('error', error.message);

    setCompanyData(companyDataWithFullName);
    updateUserData({
      memberships: user?.memberships?.map(membership =>
        membership.company.id === companyDataWithFullName.id
          ? {
              ...membership,
              company: Object.assign(membership.company, {
                full_name: companyDataWithFullName.full_name,
              }),
            }
          : membership
      ),
    });

    Toast('success', 'Nome cadastrado com sucesso!');
    setIsOpenModalRegisterFullName(false);
  };

  const setCompanyDataById = async (companyId: number) => {
    const { data } = await api.get<ICompany>(`/api/companies/${companyId}/`);
    setCompanyData(data);
  };

  const requestValidationByMembership = async (membership: IUserMembership) => {
    await api.post(`/api/companies/${membership.company.id}/request_access/`);
    updateUserData({
      memberships: user.memberships?.concat({
        ...membership,
        has_requested_validation: true,
      }),
    });
  };

  const submitConfirmData = async (formValues: { short_name: string }) => {
    if (!companyData.full_name) {
      setIsOpenModalRegisterFullName(true);
      return;
    }
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);

      if (!companyData.id) return;

      if (isAlreadyAffiliateToCompany) {
        if (!companyData.short_name) {
          await updateCompany({
            id: companyData.id,
            short_name: formValues.short_name,
            full_name: companyData.full_name,
          });
        }
        await setCompanyDataById(companyData.id);
        onAdvancedStep();
        return;
      }

      const newMembership = await createMembership(companyData.id);

      if (!companyData.short_name) {
        await updateCompany({
          id: companyData.id,
          short_name: formValues.short_name,
          full_name: companyData.full_name,
        });
      }

      if (!newMembership.company.has_confirmed_configuration) {
        await setCompanyDataById(newMembership.company.id);
        onAdvancedStep();
        return;
      }

      if (
        newMembership.company.has_confirmed_configuration &&
        newMembership.is_superadmin
      ) {
        onSuccess();
        Toast(
          'success',
          `Parabéns! Você se tornou administrador da empresa ${companyData.full_name}`
        );
        return;
      }

      if (newMembership.company.has_confirmed_configuration) {
        await requestValidationByMembership(newMembership);
        onSuccess();
        Toast(
          'success',
          `Parabéns! Você se tornou membro da empresa ${companyData.full_name}`
        );
        return;
      }
    }).finally(() => setIsLoading(false));

    if (error) return Toast('error', error.message);
  };

  async function createMembership(companyId: number) {
    const { data: membership } = await api.post<IUserMembership>(
      `/api/companies/${companyId}/affiliate/`
    );

    updateUserData({
      memberships: user.memberships?.concat([membership]),
    });

    return membership;
  }

  async function updateCompany({ id, ...companyUpdated }: Partial<ICompany>) {
    return api.patch(`api/companies/${id}/`, companyUpdated);
  }

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          opacity: '50%',
          marginTop: 'calc(17vh - 24px)',
          marginBottom: '16px',
          [themeLight.breakpoints.up('md')]: {
            marginTop: '0px',
          },
        }}
      >
        Confirme os dados
      </Typography>

      <CustomForm
        buttonText="Próximo"
        onSubmit={submitConfirmData}
        initialValues={{
          short_name: companyData.short_name || '',
        }}
        validationSchema={confirmDataForm}
      >
        <Box>
          <ConfirmDataRow
            title="CNPJ"
            content={cnpjMask(companyData?.cnpj || '')}
          />
          {companyData?.short_name && (
            <ConfirmDataRow
              title="Nome fantasia"
              content={companyData?.short_name}
            />
          )}
          {companyData?.full_name && (
            <ConfirmDataRow
              title="Razão social"
              content={companyData?.full_name}
            />
          )}
          {companyData?.state_registration && (
            <ConfirmDataRow
              title="Inscrição estadual"
              content={companyData?.state_registration}
            />
          )}

          {!companyData?.short_name && (
            <CustomTextField
              variant="standard"
              label="Nome fantasia*"
              name="short_name"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <EditOutlinedIcon />
                  </InputAdornment>
                ),
                inputProps: {
                  'aria-required': 'true',
                },
              }}
            />
          )}
        </Box>
      </CustomForm>

      <Modal
        open={isOpenModalRegisterFullName}
        aria-labelledby="modal-inform-full-name-title"
        aria-describedby="modal-inform-full-name-description"
      >
        <Box
          sx={{
            textAlign: 'center',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 350,
            bgcolor: 'background.paper',
            boxShadow: 24,
            px: 3,
            py: 4,
            borderRadius: '4px',
          }}
        >
          <Box sx={{ margin: '-24px -16px 0 0' }}>
            <IconButton
              onClick={() => setIsOpenModalRegisterFullName(false)}
              sx={{
                marginLeft: 'auto',
                display: 'flex',
                color: 'text.primary',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Image width="236px" height="143px" src={NotifyImg} alt="" />
          </Box>
          <Box component="form" onSubmit={handleSubmitRegisterFullName}>
            <Box
              sx={{
                mb: 1,
              }}
            >
              <Typography sx={{ mt: 2 }} variant="h6">
                Ops! <br /> Não encontramos o nome da sua empresa.
              </Typography>
              <Typography
                variant="body2"
                sx={{ my: 1, color: 'text.secondary' }}
              >
                Faça o cadastro rapidamente.
              </Typography>
            </Box>
            <TextField
              placeholder="Nome da sua empresa"
              name="full_name"
              label="Nome*"
              variant="standard"
              fullWidth
            />
            <Button sx={{ mt: 4 }} type="submit" variant="contained" fullWidth>
              Cadastrar
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export type { IConfirmDataProps };
export default ConfirmData;
